// V1.9 — Objective Pulse Layer. A glowing, pulsing ring rendered at a
// world-space target position to draw the player's eye to a tutorial
// objective (currently: the first neutral relay during the capture
// step). Pure presentation — drives off `setTarget(x, y)` /
// `clearTarget()` calls from the tutorial logic; never touches sim
// state.
//
// Why a dedicated layer (vs. piggybacking on EffectsLayer):
//   - EffectsLayer is snapshot-diff-driven and runs hot every tick. The
//     objective pulse needs to *persist* until the player completes the
//     step, which is at-most one entity over many frames — different
//     lifecycle, different update cadence.
//   - The ring needs to sit ABOVE the relay sprite (depth: very high)
//     and pulse on a self-contained tween, not a Phaser.Group.
//
// Implementation:
//   - One Phaser.GameObjects.Graphics persistent for the scene lifetime.
//   - A pair of Phaser tweens (scale + alpha) running in yoyo for the
//     pulsing animation; stopped/destroyed on dispose.
//   - `setTarget` repositions the graphics and starts the tween;
//     `clearTarget` hides it and pauses the tween.

import Phaser from 'phaser';

const RING_BASE_RADIUS = 64;
const RING_LINE_WIDTH = 3;
const RING_COLOR = 0xf5e76e; // yellow — matches the EOM tip / unlock chip
const PULSE_MIN_SCALE = 0.7;
const PULSE_MAX_SCALE = 1.18;
const PULSE_DURATION_MS = 720;
const DEPTH = 9999;

export class ObjectivePulseLayer {
  private readonly scene: Phaser.Scene;
  private readonly graphics: Phaser.GameObjects.Graphics;
  private tween: Phaser.Tweens.Tween | null = null;
  private active = false;
  private disposed = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.graphics = scene.add.graphics();
    this.graphics.setDepth(DEPTH);
    this.graphics.setVisible(false);
    this.drawRing();
  }

  setTarget(worldX: number, worldY: number): void {
    if (this.disposed) return;
    this.graphics.setPosition(worldX, worldY);
    this.graphics.setVisible(true);
    if (this.active) return;
    this.active = true;
    this.graphics.setAlpha(0.95);
    this.graphics.setScale(PULSE_MIN_SCALE);
    this.tween = this.scene.tweens.add({
      targets: this.graphics,
      scale: PULSE_MAX_SCALE,
      alpha: 0.35,
      duration: PULSE_DURATION_MS,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  clearTarget(): void {
    if (!this.active) {
      this.graphics.setVisible(false);
      return;
    }
    this.active = false;
    this.graphics.setVisible(false);
    if (this.tween !== null) {
      this.tween.stop();
      this.tween = null;
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.tween !== null) {
      this.tween.stop();
      this.tween = null;
    }
    this.graphics.destroy();
  }

  private drawRing(): void {
    this.graphics.clear();
    // Outer ring — main attention-grabber.
    this.graphics.lineStyle(RING_LINE_WIDTH, RING_COLOR, 1);
    this.graphics.strokeCircle(0, 0, RING_BASE_RADIUS);
    // Inner hairline ring — depth cue so the ring doesn't read as a flat
    // sticker on the relay sprite.
    this.graphics.lineStyle(1, RING_COLOR, 0.55);
    this.graphics.strokeCircle(0, 0, RING_BASE_RADIUS - 6);
  }
}
